import React from 'react';
import Blog from '../Blog';
import Brands from '../Home/Brands';
import Features from '../Home/Features';
import Hero from '../Home/Hero';
import Pricing from '../Home/Pricing';
import Testimonials from '../Home/Testimonials';
import Video from '../Home/Video';
import DetailSection from '../Home/Detail/DetailSection';
import CompactTeam from '../About/CompactTeam';
import ExpandedTeam from '../About/ExpandedTeam';
import FAQAccordion from '../About/FAQAccordion';
import FAQGrid from '../About/FAQGrid';
import StatsSection from '../About/StatsSection';
import AboutIntro from '../About/AboutIntro';
import {
  AboutIntroRecord,
  AllPostsSectionRecord,
  BrandSectionRecord,
  ChangelogSectionRecord,
  CollectionMetadata,
  DetailSectionRecord,
  FaqSectionRecord,
  FeatureListSectionRecord,
  FeaturedPostsSectionRecord,
  HeroSectionRecord,
  PageModelSectionsField,
  PostRecord,
  PricingSectionRecord,
  RedirectSectionRecord,
  ReviewSectionRecord,
  SiteLocale,
  StatsSectionRecord,
  TeamSectionRecord,
  VideoSectionRecord,
} from '@/graphql/types/graphql';
import GradientHero from '../Home/Hero/GradientHero';
import FeatureCards from '../Home/Features/FeatureCards';
import PostGridRenderer from '../Blog/PostGridRenderer';
import { redirect } from 'next/navigation';
import RightImageHero from '../Home/Hero/RightImageHero';
import BackgroundImageHero from '../Home/Hero/BackgroundImage';
import SplitImage from '../Home/Hero/SplitImage';
import GradientCards from '../Home/Pricing/GradientCards';
import Minimal from '../Home/Pricing/Minimal';
import FeatureListSelector from '../Home/Pricing/FeatureListSelector';
import SmallCards from '../Home/Pricing/SmallCards';
import Carrousel from '../Home/Testimonials/Carrousel';
import ModernCarrousel from '../Home/Testimonials/ModernCarrousel';
import MinimalCarrousel from '../Home/Testimonials/MinimalCarrousel';
import MinimalReviewCards from '../Home/Testimonials/MinimalReviewCards';
import BrandCards from '../Home/Brands/BrandCards';
import ModernPostCards from '../Home/Featured Posts/ModernPostCards';
import CarrouselFeaturedPosts from '../Home/Featured Posts/CarrouselFeaturedPosts';
import MinimalistFeaturedPostsGrid from '../Home/Featured Posts/MinimalistFeaturedPostsGrid';
import FullImageFeaturedPosts from '../Home/Featured Posts/FullImageFeaturedPosts';
import MinimalCardsFeature from '../Home/Features/MinimalCardsFeature';
import BigImageHorizontalFeatures from '../Home/Features/BigImageHorizontalFeatures';
import BigImageVerticalFeatures from '../Home/Features/BigImageVerticalFeatures';
import Changelog from '../Changelog';
import GoogleReviewsSection from '../Home/Testimonials/GoogleReviewsSection';
import GoogleFinanceReviewsSection from '../Home/Testimonials/GoogleFinanceReviewsSection';
import GoogleFinancialPlanningReviewsSection from '../Home/Testimonials/GoogleFinancialPlanningReviewsSection';
import GoogleInsuranceReviewsSection from '../Home/Testimonials/GoogleInsuranceReviewsSection';
import GoogleRealEstateReviewsSection from '../Home/Testimonials/GoogleRealEstateReviewsSection';

type Props = {
  sections: Array<PageModelSectionsField>;
  locale: SiteLocale;
  posts: PostRecord[];
  postMeta: CollectionMetadata;
  slug?: string;
};

export default function Section({
  sections,
  locale,
  posts,
  postMeta,
  slug,
}: Props) {
  return (
    <>
      {sections.map((section) => {
        const key = section.id;

        switch (section._modelApiKey) {
          case 'changelog_section':
            const changeLogSection = section as ChangelogSectionRecord;
            return (
              <Changelog
                key={key}
                title={changeLogSection.title}
                subtitle={changeLogSection.subtitle}
                featuredChangeLogs={changeLogSection.featuredVersions}
                locale={locale}
              />
            );
          case 'hero_section':
            const heroSectionRecord = section as HeroSectionRecord;
            switch (heroSectionRecord.displayOptions) {
              case 'gradient':
                return (
                  <GradientHero
                    key={key}
                    heroTitle={heroSectionRecord.heroTitle}
                    heroSubtitle={heroSectionRecord.heroSubtitle}
                    buttons={heroSectionRecord.buttons}
                    showJoinUsSnippet={slug === 'home'}
                  />
                );
              case 'right_image':
                return (
                  <RightImageHero
                    key={key}
                    heroTitle={heroSectionRecord.heroTitle}
                    heroSubtitle={heroSectionRecord.heroSubtitle}
                    buttons={heroSectionRecord.buttons}
                    image={heroSectionRecord.heroImage}
                    showJoinUsSnippet={slug === 'home'}
                  />
                );
              case 'background_image':
                return (
                  <BackgroundImageHero
                    key={key}
                    heroTitle={heroSectionRecord.heroTitle}
                    heroSubtitle={heroSectionRecord.heroSubtitle}
                    buttons={heroSectionRecord.buttons}
                    image={heroSectionRecord.heroImage}
                  />
                );
              case 'split_image':
                return (
                  <SplitImage
                    key={key}
                    heroTitle={heroSectionRecord.heroTitle}
                    heroSubtitle={heroSectionRecord.heroSubtitle}
                    buttons={heroSectionRecord.buttons}
                    image={heroSectionRecord.heroImage}
                  />
                );
              default:
                return (
                  <Hero
                    key={key}
                    heroTitle={heroSectionRecord.heroTitle}
                    heroSubtitle={heroSectionRecord.heroSubtitle}
                    buttons={heroSectionRecord.buttons}
                  />
                );
            }

          case 'feature_list_section':
            const featureListSectionRecord =
              section as FeatureListSectionRecord;
            switch (featureListSectionRecord.displayOption) {
              case 'card_minimal':
                return (
                  <MinimalCardsFeature
                    key={key}
                    features={featureListSectionRecord.feature}
                    featuresHeader={featureListSectionRecord.featuresHeader}
                    featuresSubheader={
                      featureListSectionRecord.featuresSubheader
                    }
                  />
                );
              case 'grid':
                return (
                  <Features
                    key={key}
                    features={featureListSectionRecord.feature}
                    featuresHeader={featureListSectionRecord.featuresHeader}
                    featuresSubheader={
                      featureListSectionRecord.featuresSubheader
                    }
                  />
                );
              case 'big_image_horizontal':
                return (
                  <BigImageHorizontalFeatures
                    key={key}
                    features={featureListSectionRecord.feature}
                    featuresHeader={featureListSectionRecord.featuresHeader}
                    featuresSubheader={
                      featureListSectionRecord.featuresSubheader
                    }
                  />
                );
              case 'big_image_vertical':
                return (
                  <BigImageVerticalFeatures
                    key={key}
                    features={featureListSectionRecord.feature}
                    featuresHeader={featureListSectionRecord.featuresHeader}
                    featuresSubheader={
                      featureListSectionRecord.featuresSubheader
                    }
                  />
                );
              default:
                return (
                  <FeatureCards
                    key={key}
                    features={featureListSectionRecord.feature}
                    featuresHeader={featureListSectionRecord.featuresHeader}
                    featuresSubheader={
                      featureListSectionRecord.featuresSubheader
                    }
                  />
                );
            }

          case 'video_section':
            const videoSectionRecord = section as VideoSectionRecord;
            return (
              <Video
                key={key}
                videoHeader={videoSectionRecord.videoHeader}
                videoSubheader={videoSectionRecord.videoSubheader}
                videoUid={videoSectionRecord.video?.providerUid}
                videoThumbnail={videoSectionRecord.videoThumbnail}
                videoProvider={videoSectionRecord.video?.provider}
              />
            );
          case 'brand_section':
            const brandSectionRecord = section as BrandSectionRecord;
            switch (brandSectionRecord.displayOptions) {
              case 'brand_cards':
                return (
                  <BrandCards
                    key={key}
                    brandShowcase={brandSectionRecord.brand}
                  />
                );
              default:
                return (
                  <Brands key={key} brandShowcase={brandSectionRecord.brand} />
                );
            }
          case 'detail_section':
            const detailSectionRecord = section as DetailSectionRecord;
            return (
              <DetailSection
                key={key}
                imagePosition={detailSectionRecord.imagePosition as boolean}
                image={detailSectionRecord.image}
                details={detailSectionRecord.details}
              />
            );
          case 'review_section':
            const reviewSectionRecord = section as ReviewSectionRecord;
            // On the homepage, use live Google Reviews with DatoCMS testimonials as fallback
            if (slug === 'home') {
              return (
                <GoogleReviewsSection
                  key={key}
                  header={reviewSectionRecord.reviewSectionHeader}
                  subheader={
                    reviewSectionRecord.reviewSectionSubheader || undefined
                  }
                  fallbackReviews={reviewSectionRecord.reviews}
                  display={reviewSectionRecord.displayOptions}
                />
              );
            }

            // On the home-loans page, use finance-specific Google Reviews with DatoCMS testimonials as fallback
            if (slug === 'home-loans') {
              return (
                <GoogleFinanceReviewsSection
                  key={key}
                  header={reviewSectionRecord.reviewSectionHeader}
                  subheader={
                    reviewSectionRecord.reviewSectionSubheader || undefined
                  }
                  fallbackReviews={reviewSectionRecord.reviews}
                  display={reviewSectionRecord.displayOptions}
                />
              );
            }

            // On the mackay-financial-planning page, use financial-planning specific Google Reviews with DatoCMS testimonials as fallback
            if (slug === 'mackay-financial-planning') {
              return (
                <GoogleFinancialPlanningReviewsSection
                  key={key}
                  header={reviewSectionRecord.reviewSectionHeader}
                  subheader={
                    reviewSectionRecord.reviewSectionSubheader || undefined
                  }
                  fallbackReviews={reviewSectionRecord.reviews}
                  display={reviewSectionRecord.displayOptions}
                />
              );
            }

            // On the gardian-insurance page, use insurance-specific Google Reviews with DatoCMS testimonials as fallback
            if (slug === 'gardian-insurance') {
              return (
                <GoogleInsuranceReviewsSection
                  key={key}
                  header={reviewSectionRecord.reviewSectionHeader}
                  subheader={
                    reviewSectionRecord.reviewSectionSubheader || undefined
                  }
                  fallbackReviews={reviewSectionRecord.reviews}
                  display={reviewSectionRecord.displayOptions}
                />
              );
            }

            // On the real-estate page, use real-estate specific Google Reviews with DatoCMS testimonials as fallback
            if (slug === 'real-estate') {
              return (
                <GoogleRealEstateReviewsSection
                  key={key}
                  header={reviewSectionRecord.reviewSectionHeader}
                  subheader={
                    reviewSectionRecord.reviewSectionSubheader || undefined
                  }
                  fallbackReviews={reviewSectionRecord.reviews}
                  display={reviewSectionRecord.displayOptions}
                />
              );
            }

            switch (reviewSectionRecord.displayOptions) {
              case 'card_carrousel':
                return (
                  <Carrousel
                    key={key}
                    header={reviewSectionRecord.reviewSectionHeader}
                    subheader={reviewSectionRecord.reviewSectionSubheader}
                    reviews={reviewSectionRecord.reviews}
                  />
                );
              case 'modern_carrousel':
                return (
                  <ModernCarrousel
                    key={key}
                    header={reviewSectionRecord.reviewSectionHeader}
                    subheader={reviewSectionRecord.reviewSectionSubheader}
                    reviews={reviewSectionRecord.reviews}
                  />
                );
              case 'minimal_carrousel':
                return (
                  <MinimalCarrousel
                    key={key}
                    header={reviewSectionRecord.reviewSectionHeader}
                    subheader={reviewSectionRecord.reviewSectionSubheader}
                    reviews={reviewSectionRecord.reviews}
                  />
                );
              case 'minimal_cards':
                return (
                  <MinimalReviewCards
                    key={key}
                    header={reviewSectionRecord.reviewSectionHeader}
                    subheader={reviewSectionRecord.reviewSectionSubheader}
                    reviews={reviewSectionRecord.reviews}
                  />
                );
              default:
                return (
                  <Testimonials
                    key={key}
                    header={reviewSectionRecord.reviewSectionHeader}
                    subheader={reviewSectionRecord.reviewSectionSubheader}
                    reviews={reviewSectionRecord.reviews}
                  />
                );
            }

          case 'pricing_section':
            const pricingSectionRecord = section as PricingSectionRecord;
            switch (pricingSectionRecord.displayOption) {
              case 'cards_gradient':
                return (
                  <GradientCards
                    key={key}
                    header={pricingSectionRecord.pricingSectionHeader}
                    subheader={pricingSectionRecord.pricingSectionSubheader}
                    plans={pricingSectionRecord.plans}
                  />
                );
              case 'minimal':
                return (
                  <Minimal
                    key={key}
                    header={pricingSectionRecord.pricingSectionHeader}
                    subheader={pricingSectionRecord.pricingSectionSubheader}
                    plans={pricingSectionRecord.plans}
                  />
                );
              case 'feature_list':
                return (
                  <FeatureListSelector
                    key={key}
                    header={pricingSectionRecord.pricingSectionHeader}
                    subheader={pricingSectionRecord.pricingSectionSubheader}
                    plans={pricingSectionRecord.plans}
                  />
                );
              case 'mini_cards':
                return (
                  <SmallCards
                    key={key}
                    header={pricingSectionRecord.pricingSectionHeader}
                    subheader={pricingSectionRecord.pricingSectionSubheader}
                    plans={pricingSectionRecord.plans}
                  />
                );
              default:
                return (
                  <Pricing
                    key={key}
                    header={pricingSectionRecord.pricingSectionHeader}
                    subheader={pricingSectionRecord.pricingSectionSubheader}
                    plans={pricingSectionRecord.plans}
                  />
                );
            }

          case 'featured_posts_section':
            const featuredPostsSectionRecord =
              section as FeaturedPostsSectionRecord;
            switch (featuredPostsSectionRecord.displayOptions) {
              case 'modern_cards':
                return (
                  <ModernPostCards
                    key={key}
                    locale={locale}
                    blogData={featuredPostsSectionRecord.featuredPosts}
                    blogHeader={featuredPostsSectionRecord.featuredPostsHeader}
                    blogSubheader={
                      featuredPostsSectionRecord.featuredPostsSubheader
                    }
                  />
                );
              case 'carrousel':
                return (
                  <CarrouselFeaturedPosts
                    key={key}
                    locale={locale}
                    blogData={featuredPostsSectionRecord.featuredPosts}
                    blogHeader={featuredPostsSectionRecord.featuredPostsHeader}
                    blogSubheader={
                      featuredPostsSectionRecord.featuredPostsSubheader
                    }
                  />
                );
              case 'minimalist_grid':
                return (
                  <MinimalistFeaturedPostsGrid
                    key={key}
                    locale={locale}
                    blogData={featuredPostsSectionRecord.featuredPosts}
                    blogHeader={featuredPostsSectionRecord.featuredPostsHeader}
                    blogSubheader={
                      featuredPostsSectionRecord.featuredPostsSubheader
                    }
                  />
                );
              case 'full_image_card':
                return (
                  <FullImageFeaturedPosts
                    key={key}
                    locale={locale}
                    blogData={featuredPostsSectionRecord.featuredPosts}
                    blogHeader={featuredPostsSectionRecord.featuredPostsHeader}
                    blogSubheader={
                      featuredPostsSectionRecord.featuredPostsSubheader
                    }
                  />
                );
              default:
                return (
                  <Blog
                    key={key}
                    locale={locale}
                    blogData={featuredPostsSectionRecord.featuredPosts}
                    blogHeader={featuredPostsSectionRecord.featuredPostsHeader}
                    blogSubheader={
                      featuredPostsSectionRecord.featuredPostsSubheader
                    }
                  />
                );
            }

          case 'team_section':
            const teamSectionRecord = section as TeamSectionRecord;
            if (teamSectionRecord.displayOptions === 'compact')
              return (
                <CompactTeam
                  key={key}
                  header={teamSectionRecord.title}
                  subheader={teamSectionRecord.subtitle}
                  members={teamSectionRecord.showcasedMembers}
                  lng={locale}
                />
              );
            return (
              <ExpandedTeam
                key={key}
                header={teamSectionRecord.title}
                subheader={teamSectionRecord.subtitle}
                members={teamSectionRecord.showcasedMembers}
                lng={locale}
              />
            );
          case 'faq_section':
            const faqSectionRecord = section as FaqSectionRecord;
            if (faqSectionRecord.displayOptions === 'accordion')
              return (
                <FAQAccordion
                  key={key}
                  title={faqSectionRecord.title}
                  subtitle={faqSectionRecord.subtitle}
                  questions={faqSectionRecord.questions}
                />
              );
            return (
              <FAQGrid
                key={key}
                title={faqSectionRecord.title}
                subtitle={faqSectionRecord.subtitle}
                questions={faqSectionRecord.questions}
              />
            );
          case 'stats_section':
            const statsSectionRecord = section as StatsSectionRecord;
            return (
              <StatsSection
                key={key}
                title={statsSectionRecord.title}
                subtitle={statsSectionRecord.subtitle}
                statistic={statsSectionRecord.statistic}
              />
            );
          case 'about_intro':
            const aboutIntroSectionRecord = section as AboutIntroRecord;
            return (
              <AboutIntro
                key={key}
                header={aboutIntroSectionRecord.header}
                subheader={aboutIntroSectionRecord.subheader}
                introduction={aboutIntroSectionRecord.introductionText}
                images={aboutIntroSectionRecord.images}
                preHeader={aboutIntroSectionRecord.preHeader}
              />
            );
          case 'all_posts_section':
            const allPostsSectionRecord = section as AllPostsSectionRecord;
            return (
              <PostGridRenderer
                key={key}
                data={posts}
                lng={locale}
                postMeta={postMeta}
              />
            );
          case 'redirect_section':
            const redirectSectionRecord = section as RedirectSectionRecord;
            redirect(`/${locale}/${redirectSectionRecord.slugToRedirectTo}`);
          default:
            return <></>;
        }
      })}
    </>
  );
}
